import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function FoxHedgePage() {
  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Fox Hedge",
      "description": "Daily hedge-planting puzzle: pen the fox in the most valuable meadow you can with a limited number of hedges.",
      "url": "https://logicpuzzles.ca/games/fox-hedge",
      "image": "https://logicpuzzles.ca/assets/images/gameplay/fox-hedge.png",
      "genre": "Logic Puzzle",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/foxhedge"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Fox Hedge - Daily Pen Building Puzzle | Logic Puzzles CA</title>
        <meta name="description" content="Plant a limited number of hedges to pen the fox in the best meadow you can. Fit in mushrooms, keep out the hens, and match the computer-checked par for three stars." />
        <meta name="keywords" content="fox hedge puzzle, daily pen puzzle, grid logic game, hedge placement puzzle, logic puzzle" />
        <meta property="og:title" content="Fox Hedge - Daily Pen Building Puzzle" />
        <meta property="og:description" content="Plant limited hedges to pen the fox. Mushrooms score, hens cost, burrows connect, and every daily puzzle has a par." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/assets/images/gameplay/fox-hedge.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Fox Hedge - Daily Pen Building Puzzle" />
        <meta name="twitter:description" content="Pen the fox with a limited number of hedges. Match par for three stars." />
        <meta name="twitter:image" content="https://logicpuzzles.ca/assets/images/gameplay/fox-hedge.png" />
        <link rel="canonical" href="https://logicpuzzles.ca/games/fox-hedge" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Logic Games" categoryPath="/logic-games" currentLabel="Fox Hedge" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Daily Puzzle · Free online</p>
            <h1>Fox Hedge</h1>
            <p className="gi-hero__lead">Plant hedges to pen the fox in the best meadow you can. Every puzzle has a par, worked out by computer.</p>
            <div className="gi-hero__actions">
              <Link to="/play/foxhedge" className="button">▶ Play now</Link>
              <a href="#how-to-play" className="button secondary">Rules at a glance</a>
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/assets/images/gameplay/fox-hedge-384.webp" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="dlp" placement="game-page" page="/games/fox-hedge" headline="Enjoying Fox Hedge? Get eight new logic puzzles every day in our free app" />

        <section className="gi-section">
          <h2>Daily Pen-Building Challenge</h2>
          <p className="gi-lead">
            <strong>Fox Hedge</strong> is a daily grid puzzle where every hedge matters. Plant hedges on the
            grass so the fox can't reach the edge of the meadow, then shape that pen to be worth as much as
            possible. Each puzzle has a <strong>par</strong>: the best score possible, checked by computer, so
            you always know how close you are.
          </p>
        </section>

        <section className="gi-section" id="how-to-play">
          <h2>Rules at a Glance</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <span className="gi-card__num">01</span>
              <h3>Limited Hedges</h3>
              <p>Each puzzle gives you a set number of hedges. Tap grass to plant one, tap again to pick it up.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">02</span>
              <h3>Pen the Fox</h3>
              <p>The fox walks up, down, left and right. If it can reach the edge, the pen is open and scores nothing.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">03</span>
              <h3>Natural Walls</h3>
              <p>Water, rocks and trees block the fox for free. Build your pen around them.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">04</span>
              <h3>Burrows</h3>
              <p>Burrows come in matching pairs. The fox dives into one and pops out of its twin.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">05</span>
              <h3>Match Par</h3>
              <p>One star for any closed pen, two for 80% of par, three for matching it.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">06</span>
              <h3>Every Day</h3>
              <p>A new puzzle daily: gentle on Monday, big on Sunday. Replay past days from the calendar.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Scoring</h2>
          <p>Only a closed pen scores. Every tile the fox can reach is worth 1 point.</p>
          <ul className="gi-list">
            <li>+3 points for each mushroom inside the pen</li>
          </ul>
          <ul className="gi-list gi-list--warn">
            <li>-4 points for each hen inside the pen</li>
          </ul>
          <p>
            New to it? Six short lessons teach each idea before your first daily puzzle. Stuck? A hint shows
            one hedge from a best pen, though it caps that day at two stars.
          </p>
        </section>

        <section className="gi-cta">
          <h2>Ready to build your pen?</h2>
          <p>A new Fox Hedge puzzle every day. Free to play in your browser.</p>
          <div className="gi-cta__actions">
            <Link to="/play/foxhedge" className="button">Play Fox Hedge</Link>
            <Link to="/logic-games" className="button secondary">More Logic Puzzles</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default FoxHedgePage;
